import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ThumbsUp, ThumbsDown, Heart, Handshake, HandHeart, Loader2, CheckCircle2,
  MessageCircle, X, Landmark, Lightbulb, Search,
} from "lucide-react";
import { getClientId, CONTACT_TYPES, REASONS, BADGE_LABELS, isSeeking } from "@/lib/projectMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const cleanPayload = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => !(typeof v === "string" && v.trim() === "")));
const input = "w-full rounded-2xl border-2 border-[#E5E1D8] bg-white px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#9a9a9a] focus:border-[#2E4F3B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E4F3B]/30 transition-colors";

/* ---------- Reactions ---------- */
export function ProjectReactions({ project }) {
  const slug = project.slug;
  const [likes, setLikes] = useState(project.like_count || 0);
  const [dislikes, setDislikes] = useState(project.dislike_count || 0);
  const [mine, setMine] = useState(null);
  useEffect(() => {
    const cid = getClientId();
    fetch(`${API}/api/projects/${slug}/react?client_id=${encodeURIComponent(cid)}`).then((r) => r.json())
      .then((d) => { setLikes(d.like_count); setDislikes(d.dislike_count); setMine(d.your_reaction); }).catch(() => {});
  }, [slug]);
  const react = async (value) => {
    try {
      const d = await (await fetch(`${API}/api/projects/${slug}/react`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_id: getClientId(), value }) })).json();
      setLikes(d.like_count); setDislikes(d.dislike_count); setMine(d.your_reaction);
    } catch { toast.error("Errore, riprova."); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="project-reactions">
      <button aria-label="Mi piace" onClick={() => react("like")} data-testid="project-like"
        className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 font-bold text-sm transition-colors ${mine === "like" ? "bg-[#2E4F3B] border-[#2E4F3B] text-white" : "bg-white border-[#E5E1D8] text-[#1A1A1A] hover:border-[#2E4F3B]"}`}>
        <ThumbsUp className="w-4 h-4" /> Mi piace <span className="tabular-nums">{likes}</span>
      </button>
      <button aria-label="Non mi convince" onClick={() => react("dislike")} data-testid="project-dislike"
        className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 font-bold text-sm transition-colors ${mine === "dislike" ? "bg-[#8a4a35] border-[#8a4a35] text-white" : "bg-white border-[#E5E1D8] text-[#1A1A1A] hover:border-[#C15C3D]"}`}>
        <ThumbsDown className="w-4 h-4" /> Non mi convince <span className="tabular-nums">{dislikes}</span>
      </button>
    </div>
  );
}

/* ---------- Interest form ---------- */
function InterestForm({ slug, presetReason, formRef }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", contact_type: "cittadino", reason: "altro", message: "" });
  const [loading, setLoading] = useState(false); const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => { if (presetReason) setForm((f) => ({ ...f, reason: presetReason })); }, [presetReason]);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await fetch(`${API}/api/projects/${slug}/interest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cleanPayload(form)) });
      if (!r.ok) throw new Error("Errore durante l'invio."); setDone(true); toast.success("Richiesta inviata! Ti ricontatteremo presto.");
    } catch (err) { toast.error(err.message || "Riprova."); } finally { setLoading(false); }
  };
  if (done) return (
    <div data-testid="interest-success" className="rounded-2xl bg-[#2E4F3B] text-white p-6 text-center">
      <CheckCircle2 className="w-10 h-10 text-[#E8C9A0] mx-auto mb-2" />
      <h4 className="font-display text-lg font-black">Grazie!</h4>
      <p className="mt-1 text-white/85 text-sm">La tua richiesta è arrivata. Ti risponderemo appena possibile.</p>
    </div>
  );
  return (
    <form ref={formRef} onSubmit={submit} data-testid="interest-form" className="space-y-3 scroll-mt-24">
      <input data-testid="interest-first-name" required placeholder="Nome *" value={form.first_name} onChange={set("first_name")} className={input} />
      <input data-testid="interest-last-name" placeholder="Cognome" value={form.last_name} onChange={set("last_name")} className={input} />
      <input data-testid="interest-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={input} />
      <input data-testid="interest-phone" placeholder="Telefono (facoltativo)" value={form.phone} onChange={set("phone")} className={input} />
      <select data-testid="interest-type" value={form.contact_type} onChange={set("contact_type")} className={input}>{CONTACT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <select data-testid="interest-reason" value={form.reason} onChange={set("reason")} className={input}>{REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <textarea data-testid="interest-message" rows={3} placeholder="Messaggio" value={form.message} onChange={set("message")} className={input} />
      <button type="submit" disabled={loading} data-testid="interest-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] disabled:opacity-60 text-white font-bold px-6 py-3.5 transition-colors">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />} Invia richiesta
      </button>
    </form>
  );
}

/* ---------- Idea form ---------- */
function IdeaForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", activity: "", description: "" });
  const [loading, setLoading] = useState(false); const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await fetch(`${API}/api/idea-proposals`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cleanPayload(form)) });
      if (!r.ok) throw new Error("Errore durante l'invio."); setDone(true); toast.success("Proposta inviata! Verrà valutata dal consiglio.");
    } catch (err) { toast.error(err.message || "Riprova."); } finally { setLoading(false); }
  };
  if (done) return (
    <div data-testid="idea-success" className="rounded-2xl bg-[#C15C3D] text-white p-6 text-center">
      <Lightbulb className="w-10 h-10 text-white mx-auto mb-2" />
      <h4 className="font-display text-lg font-black">Idea ricevuta!</h4>
      <p className="mt-1 text-white/90 text-sm">Grazie. La tua proposta sarà valutata dal consiglio dell'associazione.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="idea-form" className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="idea-name" required placeholder="Nome *" value={form.name} onChange={set("name")} className={input} />
        <input data-testid="idea-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={input} />
      </div>
      <input data-testid="idea-phone" placeholder="Telefono (facoltativo)" value={form.phone} onChange={set("phone")} className={input} />
      <input data-testid="idea-activity" required placeholder="Che tipo di attività proponi? *" value={form.activity} onChange={set("activity")} className={input} />
      <textarea data-testid="idea-description" required rows={4} placeholder="Descrivi la tua idea *" value={form.description} onChange={set("description")} className={input} />
      <button type="submit" disabled={loading} data-testid="idea-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1f2d22] hover:bg-[#16201a] disabled:opacity-60 text-white font-bold px-6 py-3.5 transition-colors">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />} Proponi la tua idea
      </button>
    </form>
  );
}

/* ---------- Sidebar (donation + needs + support + forms) ---------- */
export function ProjectSidebar({ project }) {
  const [presetReason, setPresetReason] = useState("altro");
  const formRef = useRef(null);
  const slug = project.slug;
  const goToForm = (reason) => { setPresetReason(reason); setTimeout(() => formRef.current && formRef.current.scrollIntoView({ behavior: "smooth", block: "center" }), 60); };
  const hasTarget = project.fund_target && project.fund_target > 0;
  const raised = project.fund_raised || 0;
  const pct = hasTarget ? Math.min(100, Math.round((raised / project.fund_target) * 100)) : 0;
  const seeking = (project.badges || []).filter(isSeeking);

  return (
    <div className="space-y-5">
      {/* Donation */}
      <div className="rounded-3xl bg-gradient-to-br from-[#2E4F3B] to-[#1f2d22] text-white p-6" data-testid="project-donation">
        <div className="flex items-center gap-2 mb-2"><Landmark className="w-4 h-4 text-[#E8C9A0]" /><span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8C9A0]">Sostieni</span></div>
        <h3 className="font-display text-xl font-black">Aiutaci a realizzarlo</h3>
        {hasTarget && (
          <div className="mt-4">
            <div className="flex justify-between text-xs font-bold mb-1.5"><span>€ {raised.toLocaleString("it-IT")}</span><span className="text-white/70">Obiettivo € {project.fund_target.toLocaleString("it-IT")}</span></div>
            <div className="h-2.5 rounded-full bg-white/15 overflow-hidden" role="progressbar"><div className="h-full rounded-full bg-[#E8C9A0]" style={{ width: `${pct}%` }} /></div>
            {project.supporters_count > 0 && <p className="mt-1.5 text-xs text-white/70">{project.supporters_count} sostenitori</p>}
          </div>
        )}
        <p className="mt-3 text-sm text-white/80 leading-relaxed">Stiamo attivando il conto dedicato: a breve qui le coordinate per donare. Intanto comunicaci il tuo sostegno.</p>
        <button onClick={() => goToForm("donazione")} data-testid="project-donate" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold px-5 py-3 transition-colors"><Heart className="w-4 h-4" /> Dona a questo progetto</button>
      </div>

      {/* Needs */}
      {seeking.length > 0 && (
        <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6">
          <h4 className="font-display text-base font-black text-[#1A1A1A] mb-3">Cosa cerchiamo</h4>
          <div className="flex flex-wrap gap-2">
            {seeking.map((b) => <span key={b} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#C15C3D] text-white">{BADGE_LABELS[b]}</span>)}
          </div>
        </div>
      )}

      {/* Support CTAs + interest form */}
      <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6" data-testid="project-support">
        <h3 className="font-display text-lg font-black text-[#1A1A1A]">Vuoi contribuire?</h3>
        <div className="mt-4 grid grid-cols-1 gap-2.5">
          <button onClick={() => goToForm("donazione")} data-testid="support-sostienilo" className="flex items-center gap-3 rounded-2xl bg-[#FBF9F6] border-2 border-[#E5E1D8] hover:border-[#C15C3D] p-3.5 text-left transition-colors"><Heart className="w-5 h-5 text-[#C15C3D] shrink-0" /><span className="font-bold text-sm text-[#1A1A1A]">Voglio sostenerlo</span></button>
          <button onClick={() => goToForm("partner")} data-testid="support-partner" className="flex items-center gap-3 rounded-2xl bg-[#FBF9F6] border-2 border-[#E5E1D8] hover:border-[#2E4F3B] p-3.5 text-left transition-colors"><Handshake className="w-5 h-5 text-[#2E4F3B] shrink-0" /><span className="font-bold text-sm text-[#1A1A1A]">Voglio diventare partner</span></button>
          <button onClick={() => goToForm("volontario")} data-testid="support-partecipa" className="flex items-center gap-3 rounded-2xl bg-[#FBF9F6] border-2 border-[#E5E1D8] hover:border-[#8B5E3C] p-3.5 text-left transition-colors"><HandHeart className="w-5 h-5 text-[#8B5E3C] shrink-0" /><span className="font-bold text-sm text-[#1A1A1A]">Voglio partecipare</span></button>
        </div>
        <p className="mt-5 text-sm font-semibold text-[#1A1A1A]">Scrivici o proponi un'idea 👇</p>
        <div className="mt-3"><InterestForm slug={slug} presetReason={presetReason} formRef={formRef} /></div>
      </div>

      {/* Idea form (only for network project) */}
      {project.enable_idea_form && (
        <div className="rounded-3xl border-2 border-[#C15C3D]/40 bg-[#FBF9F6] p-6" data-testid="project-idea-box">
          <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-[#C15C3D]" /><span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C15C3D]">Proponi un'attività</span></div>
          <h3 className="font-display text-lg font-black text-[#1A1A1A]">Hai un'idea di nuova attività?</h3>
          <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">Proponi il tipo di attività che vorresti far nascere sul territorio. Le proposte vengono valutate da un consiglio dedicato.</p>
          <div className="mt-4"><IdeaForm /></div>
        </div>
      )}
    </div>
  );
}

/* ---------- Comments ---------- */
export function ProjectComments({ slug }) {
  const [comments, setComments] = useState([]);
  const [idx, setIdx] = useState(0); const [show, setShow] = useState(true); const [openAll, setOpenAll] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", email: "", text: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const load = () => fetch(`${API}/api/projects/${slug}/comments`).then((r) => r.json()).then(setComments).catch(() => {});
  useEffect(() => { load(); }, [slug]);
  useEffect(() => {
    if (comments.length < 2) return;
    const t = setInterval(() => { setShow(false); setTimeout(() => { setIdx((i) => (i + 1) % comments.length); setShow(true); }, 600); }, 5000);
    return () => clearInterval(t);
  }, [comments]);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await fetch(`${API}/api/projects/${slug}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cleanPayload(form)) });
      if (!r.ok) throw new Error("Errore."); setForm({ name: "", surname: "", email: "", text: "" }); toast.success("Grazie! Il commento sarà pubblicato dopo la moderazione.");
    } catch { toast.error("Riprova."); } finally { setLoading(false); }
  };
  const c = comments[idx];
  return (
    <div data-testid="project-comments">
      <h3 className="font-display text-2xl font-black text-[#1A1A1A] mb-1">Le opinioni della comunità</h3>
      <p className="text-sm text-[#5C5C5C] mb-5">I commenti vengono pubblicati dopo l'approvazione dell'associazione.</p>
      {comments.length > 0 && (
        <div className="rounded-3xl bg-[#1f2d22] text-white p-8 min-h-[150px] flex flex-col justify-center relative overflow-hidden">
          <MessageCircle className="absolute -top-4 -right-2 w-24 h-24 text-white/5" />
          <div className={`transition-opacity duration-500 ${show ? "opacity-100" : "opacity-0"}`} data-testid="comment-rotator">
            <p className="font-display text-lg sm:text-xl leading-snug italic">"{c?.text}"</p>
            <p className="mt-3 text-sm text-[#E8C9A0] font-bold">— {c?.name}{c?.surname ? ` ${c.surname[0]}.` : ""}</p>
          </div>
          <button onClick={() => setOpenAll(true)} data-testid="comments-read-all" className="mt-5 self-start text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white underline underline-offset-4">Leggi tutti ({comments.length})</button>
        </div>
      )}
      <form onSubmit={submit} data-testid="comment-form" className="mt-6 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="comment-name" required placeholder="Nome *" value={form.name} onChange={set("name")} className={input} />
          <input data-testid="comment-surname" placeholder="Cognome (facoltativo)" value={form.surname} onChange={set("surname")} className={input} />
        </div>
        <input data-testid="comment-email" type="email" placeholder="Email (non pubblicata, solo anti-spam)" value={form.email} onChange={set("email")} className={input} />
        <textarea data-testid="comment-text" required rows={3} placeholder="Cosa ne pensi? Aiutaci a migliorarlo. *" value={form.text} onChange={set("text")} className={input} />
        <button type="submit" disabled={loading} data-testid="comment-submit" className="inline-flex items-center gap-2 rounded-full bg-[#2E4F3B] hover:bg-[#264332] disabled:opacity-60 text-white font-bold px-6 py-3 transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Invia commento
        </button>
      </form>
      {openAll && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setOpenAll(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h4 className="font-display text-xl font-black text-[#1A1A1A]">Tutti i commenti</h4><button onClick={() => setOpenAll(false)} data-testid="comments-close"><X className="w-5 h-5 text-[#5C5C5C]" /></button></div>
            <div className="space-y-4">{comments.map((cm) => (<div key={cm.id} className="border-b border-[#E5E1D8] pb-3"><p className="text-[#1A1A1A] text-sm leading-relaxed">"{cm.text}"</p><p className="mt-1.5 text-xs text-[#C15C3D] font-bold">— {cm.name}{cm.surname ? ` ${cm.surname[0]}.` : ""}</p></div>))}</div>
          </div>
        </div>
      )}
    </div>
  );
}
